import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Text, usePaginationParams } from '@common-fe/common-fe';

import useGetContactLoginHistoryQuery from '@/modules/contact/ContactLoginHistory/queries/useGetContactLoginHistory.query';
import Topbar from '@/modules/core/components/Topbar/Topbar';
import { useCurrentOrganisationStore } from '@/modules/core/store';
import LoginHistoryList from '@/modules/LoginHistory/LoginHistoryList';
import globalTheme from '@/styles/theme';
import { useFilters } from '@/utils';

import ContactLoginHistoryHeader from './components/ContactLoginHistoryHeader';

const DEFAULT_PAGE_SIZE = 10;

const ContactLoginHistory = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultContactId = queryParams.get('contactId');
  const { organizationId } = useCurrentOrganisationStore();

  const { page, perPage, setPerPage, setPage } = usePaginationParams(DEFAULT_PAGE_SIZE);
  const { filtersValues, setFilterValue } = useFilters<{
    contactId: string | null,
    startDate: Date | null,
    endDate: Date | null,
  }>({ contactId: defaultContactId, startDate: null, endDate: null }, { autoClear: true });

  const {
    contactLoginHistory,
    total,
    isLoading,
  } = useGetContactLoginHistoryQuery({
    contactId: filtersValues.contactId,
    startDate: filtersValues.startDate,
    endDate: filtersValues.endDate,
    page,
    perPage,
  });

  const handleSetPeriod = useCallback((dates : [Date | null, Date | null]) => {
    if (dates[0] === null) {
      setFilterValue.startDate(null);
      setFilterValue.endDate(null);
    } else {
      setFilterValue.startDate(dates[0]);
      setFilterValue.endDate(dates[1]);
    }
  }, [setFilterValue]);

  const hasDateRangeFilter = useMemo(() => filtersValues.startDate || filtersValues.endDate, [filtersValues.startDate, filtersValues.endDate]);

  return (
    <Box>
      <Topbar />


      <Box alignSelf="center" pad="spacing24">
        <Box width={globalTheme.defaultContentWidth}>
          <ContactLoginHistoryHeader 
            contactId={filtersValues.contactId ?? undefined}
            organizationId={organizationId ?? undefined}
            setContactId={setFilterValue.contactId}
          />

          <Box margin={{ top: 'spacing24' }}>
            <LoginHistoryList
              loginHistory={contactLoginHistory}
              isLoading={isLoading}
              total={total}
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={setPerPage}
              startDate={filtersValues.startDate}
              endDate={filtersValues.endDate}
              setPeriod={handleSetPeriod}
              placeholder={(
                <Text data-testId="login-history-list_placeholder" textAlign="center" size="medium" weight="bold" margin={{ bottom: 's' }}>
                  No authentication history {hasDateRangeFilter ? '' : '(contact has not logged in)'}
                </Text>
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactLoginHistory;
